import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from '../components/layout/AuthLayout';
import Input, { EmailIcon } from '../components/ui/Input';
import Button from '../components/ui/Button';
import { authService } from '../services/authService';
import './Auth.css';

const ForgotPassword = () => {
  const [formData, setFormData] = useState({
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      const response = await authService.forgotPassword(formData.email);
      console.log('Password reset email sent:', response);
      setSuccess(true);
    } catch (error) {
      console.error('Password reset failed:', error);
      setErrors({ 
        general: error.response?.data?.message || 'Failed to send reset email. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="auth-form">
          <div className="auth-header">
            <h1 className="auth-title">Check your email</h1>
          </div>

          <div className="auth-content">
            <div className="success-message">
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="32" fill="#1E88E5" fillOpacity="0.1"/>
                  <circle cx="32" cy="32" r="20" fill="#1E88E5"/>
                  <path d="M24 32L29 37L40 26" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="success-text">
                We've sent a password reset link to <strong>{formData.email}</strong>
              </p>
              <p className="success-subtext">
                Please check your email and click the link to reset your password. 
                If you don't see the email, check your spam folder.
              </p>
            </div>

            <div className="form-actions">
              <Button
                onClick={() => {
                  setSuccess(false);
                  setFormData({ email: '' });
                }}
                variant="outline"
                size="large"
                className="submit-button"
              >
                Try another email
              </Button>
              
              <div className="auth-footer">
                <span className="auth-footer-text">Remember your password?</span>
                <Link to="/login" className="auth-footer-link">
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="auth-form">
        <div className="auth-header">
          <h1 className="auth-title">Forgot your password?</h1>
          <p className="auth-subtitle">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <div className="auth-content">
          <form onSubmit={handleSubmit} className="auth-form-fields">
            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email address</label>
              <Input
                type="email"
                name="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleChange}
                icon={EmailIcon}
                error={errors.email}
              />
            </div>

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={loading}
                className="submit-button"
              >
                Send Reset Link
              </Button>
              
              <div className="auth-footer">
                <span className="auth-footer-text">Remember your password?</span>
                <Link to="/login" className="auth-footer-link">
                  Back to Sign In
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
